import store from '@/store';
import {fetchAuthSession} from "@aws-amplify/core";

class WebSocketService {
    constructor() {
        this.ws = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
        this.reconnectTimer = null;
        this.isIntentionalClose = false;
        this._pendingToken = null;
        this._markReadTimers = {};
    }

    connect(token) {

        const wsUrl = store.getters['chat/websocketUrl'];

        if (!wsUrl) {
            console.error('WebSocket URL not available');
            return;
        }

        // Token is sent as the first message after connection, not in the URL,
        // to avoid it appearing in server logs, browser history, and Referer headers.
        this._pendingToken = token;
        this.isIntentionalClose = false;

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
            this.ws.send(JSON.stringify({ action: 'auth', token: this._pendingToken }));
            this._pendingToken = null;
        };

        this.ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
        };

        this.ws.onclose = async () => {
            store.commit('chat/SET_CONNECTION_STATUS', 'disconnected');
            if (!this.isIntentionalClose) {
                await this.scheduleReconnect();
            }
        };

        this.ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            store.commit('chat/SET_CONNECTION_STATUS', 'error');
        };
    }

    handleMessage(data) {
        switch (data.type) {
            case 'authSuccess': {
                this.reconnectAttempts = 0;
                store.commit('chat/SET_CONNECTION_STATUS', 'connected');
                break;
            }
            case 'authFailed': {
                console.error('WebSocket auth failed:', data.message);
                store.commit('chat/SET_CONNECTION_STATUS', 'error');
                this.isIntentionalClose = true;
                this.ws?.close();
                break;
            }
            case 'newMessage': {
                let currentUserID = store.getters['auth/getCurrentUserID'];
                store.commit('chat/REPLACE_OR_ADD_MESSAGE', {
                    message: data.message,
                    currentUserID: currentUserID,
                });
                store.commit('chat/UPDATE_ROOM_LAST_MESSAGE', data.message);
                // if room in which message was sent is opened, mark it as read
                let activeRoom = store.getters["chat/activeRoom"];
                if (data?.message?.RoomID === activeRoom?.RoomID) {
                    this._debouncedMarkRoomAsRead(data.message.RoomID);
                }

                break;
            }
            case 'roomCreated': {
                store.commit('chat/ADD_ROOM', data.room);
                if (data.room.Type === 'group' && data.room.Picture) {
                    store.dispatch('file/getFileURL', {
                        fileName: data.room.Picture,
                        folderName: `chat/${data.room.RoomID}`,
                    }).then(url => {
                        const room = store.state.chat.Rooms.find(r => r.RoomID === data.room.RoomID);
                        if (room) {
                            room.PictureURL = url;
                        }
                    });
                }
                break;
            }
            case 'roomUpdated': {
                store.commit('chat/PATCH_ROOM', data);
                if (data.Picture !== undefined) {
                    const room = store.state.chat.Rooms.find(r => r.RoomID === data.RoomID);
                    if (room && data.Picture) {
                        store.dispatch('file/getFileURL', {
                            fileName: data.Picture,
                            folderName: `chat/${data.RoomID}`,
                        }).then(url => {
                            const r = store.state.chat.Rooms.find(r => r.RoomID === data.RoomID);
                            if (r) {
                                r.PictureURL = url;
                            }
                        });
                    } else if (room && !data.Picture) {
                        room.PictureURL = null;
                    }
                }
                break;
            }
            case 'removedFromRoom': {
                store.commit('chat/REMOVE_ROOM', data.RoomID);
                break;
            }
            case 'readReceipt': {
                store.commit('chat/UPDATE_MEMBER_READ_POSITION', {
                    roomID: data.roomID,
                    userID: data.userID,
                    readAt: data.readAt,
                });
                break;
            }
            case 'reactionAdded': {
                store.commit('chat/ADD_REACTION', {
                    roomID: data.roomID,
                    messageID: data.messageID,
                    emoji: data.emoji,
                    userID: data.userID,
                });
                break;
            }
            case 'reactionRemoved': {
                store.commit('chat/REMOVE_REACTION', {
                    roomID: data.roomID,
                    messageID: data.messageID,
                    emoji: data.emoji,
                    userID: data.userID,
                });
                break;
            }
            case 'joinedRoom': {
                store.commit('chat/SET_JOINED_ROOM', data.roomID);
                break;
            }
            case 'messageEdited': {
                store.commit('chat/UPDATE_MESSAGE', {
                    roomID: data.roomID,
                    messageID: data.messageID,
                    content: data.content,
                    editedAt: data.editedAt,
                });
                if (data.newLastMessagePreview !== undefined) {
                    store.commit('chat/UPDATE_ROOM_LAST_MESSAGE_PREVIEW', {
                        roomID: data.roomID,
                        preview: data.newLastMessagePreview,
                    });
                }
                break;
            }
            case 'messageDeleted': {
                store.commit('chat/REMOVE_MESSAGE', {
                    roomID: data.roomID,
                    messageID: data.messageID,
                });
                store.commit('chat/UPDATE_ROOM_ON_MESSAGE_DELETED', {
                    roomID: data.roomID,
                    deletedMessageTimestamp: data.deletedMessageTimestamp,
                    newLastMessageAt: data.newLastMessageAt,
                    newLastMessagePreview: data.newLastMessagePreview,
                    currentUserID: store.getters['auth/getCurrentUserID'],
                });
                break;
            }
            case 'companionStart': {
                store.commit('chat/START_STREAMING_MESSAGE', {
                    roomID: data.roomID,
                    streamMessageID: data.streamMessageID,
                });
                break;
            }
            case 'companionChunk': {
                store.commit('chat/APPEND_STREAMING_DELTA', {
                    roomID: data.roomID,
                    content: data.content,
                });
                break;
            }
            case 'companionDone': {
                const currentUserID = store.getters['auth/getCurrentUserID'];
                store.commit('chat/FINISH_STREAMING_MESSAGE', { roomID: data.roomID });
                if (data.message) {
                    store.commit('chat/REPLACE_OR_ADD_MESSAGE', {
                        message: data.message,
                        currentUserID,
                    });
                    store.commit('chat/UPDATE_ROOM_LAST_MESSAGE', data.message);
                    const activeRoom = store.getters['chat/activeRoom'];
                    if (data.message.RoomID === activeRoom?.RoomID) {
                        this._debouncedMarkRoomAsRead(data.message.RoomID);
                    }
                }
                break;
            }
            case 'companionError': {
                const currentUserID = store.getters['auth/getCurrentUserID'];
                store.commit('chat/FINISH_STREAMING_MESSAGE', { roomID: data.roomID });
                if (data.message) {
                    store.commit('chat/REPLACE_OR_ADD_MESSAGE', {
                        message: data.message,
                        currentUserID,
                    });
                    store.commit('chat/UPDATE_ROOM_LAST_MESSAGE', data.message);
                }
                break;
            }
            case 'error': {
                console.error('WebSocket server error:', data.message);
                break;
            }
        }
    }

    sendMessage(roomID, content, attachments = null) {
        const payload = { action: 'sendMessage', roomID, content };
        if (attachments && attachments.length) {
            payload.attachments = attachments;
        }
        this.send(payload);
    }

    editMessage(roomID, messageID, content) {
        this.send({ action: 'editMessage', roomID, messageID, content });
    }

    deleteMessage(roomID, messageID) {
        this.send({ action: 'deleteMessage', roomID, messageID });
    }

    joinRoom(roomID) {
        this.send({ action: 'joinRoom', roomID });
    }

    updateScreen(screen) {
        this.send({ action: 'updateScreen', screen: screen ?? null });
    }

    _debouncedMarkRoomAsRead(roomID) {
        clearTimeout(this._markReadTimers[roomID]);
        this._markReadTimers[roomID] = setTimeout(() => {
            store.dispatch('chat/markRoomAsRead', roomID);
            delete this._markReadTimers[roomID];
        }, 1500);
    }

    send(payload) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(payload));
        } else {
            console.warn('WebSocket not open, message dropped:', payload);
        }
    }

    async scheduleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnect attempts reached');
            store.commit('chat/SET_CONNECTION_STATUS', 'failed');
            return;
        }

        this.reconnectAttempts++;
        console.log(`Reconnecting in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts})`);

        // Exponential backoff + jitter so all clients don't retry simultaneously after a server blip
        const delay = Math.min(this.reconnectDelay * 2 ** (this.reconnectAttempts - 1), 30000)
            + Math.random() * 1000;
        this.reconnectTimer = setTimeout(async () => {
            const session = await fetchAuthSession({ forceRefresh: true });
            const token = session.tokens.accessToken.toString();
            if (token) {
                this.connect(token);
            }
        }, delay);
    }

    disconnect() {
        this.isIntentionalClose = true;
        clearTimeout(this.reconnectTimer);
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }
}

// Export a singleton — one connection for the whole app
export default new WebSocketService();